/**
 * MyJourneyPage — the quest list.
 *
 * The product is a journey; this is the map. A focused task list of
 * everything the user can do next to complete the chain of ancestry:
 * add missing facts, link document persons, upload documents, unlock
 * capsules, step into a world they haven't visited yet.
 *
 * Each task can be:
 *   - Opened (jumps to the relevant profile or tree view)
 *   - Dismissed ("can't resolve" — the info is genuinely lost)
 *   - Flagged to ask a relative (future: email invite with a deep link)
 *
 * Dismissed tasks stay dismissed across sessions via /archive/journey/dismiss.
 * The sidebar badge reflects the current open-count so progress is visible.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'

const JOURNEY_ICON = {
  unlock_capsule:   '✨',
  link_entities:    '🔗',
  upload_document:  '📄',
  add_birth_place:  '📍',
  enter_world:      '🌍',
  start_tree:       '🌱',
}

const KIND_LABEL = {
  unlock_capsule:   'Unlock a capsule',
  link_entities:    'Link extracted persons',
  upload_document:  'Add a document',
  add_birth_place:  'Add a birth place',
  enter_world:      'Step into a world',
  start_tree:       'Start a tree',
}

// Show this many steps per kind by default — Journey pages with 50+
// "missing birth place" items become unreadable walls of text. Users can
// expand per-kind to see the rest; as they resolve items, fresh ones
// from the next batch surface when the page refreshes.
const PER_KIND_CAP = 5

export default function MyJourneyPage() {
  usePageTitle('My Journey')
  const navigate = useNavigate()
  const [actions, setActions] = useState(null)
  const [busy, setBusy] = useState(null)
  const [expanded, setExpanded] = useState({}) // kind → true when "show all"

  async function load() {
    const list = await api.get('/archive/journey?limit=999').catch(() => [])
    setActions(list)
  }
  useEffect(() => { load() }, [])

  async function dismiss(a, reason) {
    setBusy(keyOf(a))
    try {
      await api.post('/archive/journey/dismiss', {
        kind: a.kind, person_id: a.person_id, reason,
      })
      await load()
    } finally {
      setBusy(null)
    }
  }

  if (actions === null) {
    return <div className="loading" style={{ padding: '3rem', textAlign: 'center' }}>Loading…</div>
  }

  // Group by kind so similar work stacks together.
  const grouped = {}
  for (const a of actions) {
    (grouped[a.kind] = grouped[a.kind] || []).push(a)
  }

  return (
    <div className="journey-page">
      <div className="journey-page__hero">
        <div className="journey-page__eyebrow">Your journey</div>
        <h1 className="journey-page__title">
          The quest to <em>relive</em> their lives.
        </h1>
        <p className="journey-page__lead">
          Every open step brings one more ancestor into focus. You can dismiss
          anything the records simply can't answer; the quest closes around
          what you can know.
        </p>
        <div className="journey-page__progress">
          <div className="journey-page__count">
            <span className="journey-page__count-num">{actions.length}</span>
            {' '}open {actions.length === 1 ? 'step' : 'steps'}
          </div>
        </div>
      </div>

      {actions.length === 0 ? (
        <div className="journey-page__empty">
          <div className="journey-page__empty-icon">✓</div>
          <h3>Nothing open right now.</h3>
          <p>
            Add more relatives, upload more documents, or publish a new life
            capsule — the quest picks up again.
          </p>
        </div>
      ) : (
        <div className="journey-page__groups">
          {Object.keys(grouped).map(kind => {
            const total = grouped[kind].length
            const isExpanded = !!expanded[kind]
            const visible = isExpanded ? grouped[kind] : grouped[kind].slice(0, PER_KIND_CAP)
            const hidden = total - visible.length
            return (
              <div key={kind} className="journey-group">
                <div className="journey-group__header">
                  <span className="journey-group__icon">{JOURNEY_ICON[kind] || '·'}</span>
                  <span className="journey-group__label">{KIND_LABEL[kind] || kind}</span>
                  <span className="journey-group__count">
                    {total} {total === 1 ? 'step' : 'steps'}
                  </span>
                </div>
                <div className="journey-group__list">
                  {visible.map(a => {
                    const key = keyOf(a)
                    return (
                      <div key={key} className="journey-item">
                        <div className="journey-item__main">
                          <div className="journey-item__title">{a.title}</div>
                          <div className="journey-item__body">{a.body}</div>
                        </div>
                        <div className="journey-item__actions">
                          <button className="btn btn-primary btn-sm" onClick={() => navigate(a.href)}>
                            {a.cta || 'Open'}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            disabled={busy === key}
                            onClick={() => dismiss(a, 'cant_resolve')}
                            title="The information is genuinely lost"
                            style={{ color: 'var(--text-muted)' }}
                          >
                            Can't resolve
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {total > PER_KIND_CAP && (
                  <div style={{ padding: '0.5rem 1rem 0.9rem', textAlign: 'center' }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setExpanded(e => ({ ...e, [kind]: !isExpanded }))}
                    >
                      {isExpanded ? `Show less — collapse to ${PER_KIND_CAP}` : `Show all ${total} (${hidden} more)`}
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function keyOf(a) { return `${a.kind}|${a.person_id || ''}` }
